import merge from "deepmerge";
import { green, grey, indigo, red, deepPurple } from "@mui/material/colors";
import { THEMES } from "../constants";

const customBlue = {
  50: "#e9f0fb",
  100: "#c8daf4",
  200: "#a3c1ed",
  300: "#7ea8e5",
  400: "#6395e0",
  500: "#4782da",
  600: "#407ad6",
  700: "#376fd0",
  800: "#2f65cb",
  900: "#2052c2",
};

const defaultVariant = {
  name: THEMES.DEFAULT,
  palette: {
    mode: "light",
    primary: {
      main: "#233044",
      contrastText: "#FFF",
    },
    secondary: {
      main: customBlue[500],
      contrastText: "#FFF",
    },
    background: {
      default: "#F7F9FC",
      paper: "#FFF",
    },
    neutral: {
      primary: grey[100],
      secondary: grey[200],
      tertiary: grey[300],
      quaternary: grey[400],
      contrastText: "#000",
    },
  },
  header: {
    color: "#000",
    background: "#FFF",
    search: {
      color: grey[800],
    },
    indicator: {
      background: customBlue[600],
    },
  },
  footer: {
    color: grey[500],
    background: "#FFF",
  },
  sidebar: {
    color: grey[200],
    background: "#233044",
    header: {
      color: grey[200],
      background: "#233044",
      brand: {
        color: customBlue[500],
      },
    },
    footer: {
      color: grey[200],
      background: "#1E2A38",
      online: {
        background: green[500],
      },
    },
    badge: {
      color: "#FFF",
      background: customBlue[500],
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background:
      "linear-gradient(180deg, rgba(216, 223, 247, 1) 0%, rgba(255, 255, 255, 1) 60%)",
  },
};

const darkVariant = merge(defaultVariant, {
  name: THEMES.DARK,
  palette: {
    mode: "dark",
    primary: {
      main: "#01FCFE", // customBlue[600],
      contrastText: "#000000",
    },
    secondary: {
      main: "#2F5BDB", //"#795df6", // customBlue[400],
      contrastText: "#01FCFE",
    },
    background: {
      default: "#1B2635",
      paper: "#233044",
    },
    text: {
      primary: "rgba(255, 255, 255, 0.95)",
      secondary: "rgba(255, 255, 255, 0.5)",
    },
    neutral: {
      primary: grey[900],
      secondary: grey[800],
      tertiary: grey[700],
      quaternary: grey[600],
      contrastText: "#FFF",
    },
  },
  header: {
    color: grey[200],
    background: "#1B2635",
    search: {
      color: grey[900],
    },
  },
  footer: {
    color: grey[300],
    background: "#233044",
  },
  chatbot: {
    color: "rgb(35,48,68)",
    background:
      "linear-gradient(180deg, rgba(35,48,68,1) 0%, rgba(27,38,53,1) 60%, rgba(35,48,68,1) 100%)",
  },
});

const lightVariant = merge(defaultVariant, {
  name: THEMES.LIGHT,
  palette: {
    mode: "light",
    primary: {
      main: "#2F5BDB", // customBlue[600],
      contrastText: "#01FCFE",
    },
    secondary: {
      main: "#01FCFE", //"#795df6", // customBlue[400],
      contrastText: "#000000",
    },
    text: {
      primary: "rgba(0, 0, 0, 0.95)",
      secondary: "rgba(0, 0, 0, 0.5)",
    },
    background: {
      default: "#F7F9FC",
      paper: "#ffffff",
    },
  },
  header: {
    color: "#000",
    background: "#fff", // customBlue[800],
    search: {
      color: grey[100],
    },
    indicator: {
      background: red[700],
    },
  },
  sidebar: {
    color: grey[900],
    background: "#FFF",
    header: {
      color: "#FFF",
      background: "#FFFFFF", // customBlue[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: grey[800],
      background: "#F7F7F7",
      online: {
        background: green[500],
      },
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background:
      "linear-gradient(180deg, rgba(216, 223, 247, 1) 0%, rgba(255, 255, 255, 1) 60%)",
  },
});

const blueVariant = merge(defaultVariant, {
  name: THEMES.BLUE,
  palette: {
    mode: "light",
    primary: {
      main: customBlue[700],
      contrastText: "#fff",
    },
    secondary: {
      main: customBlue[400],
      contrastText: "#000000",
    },
    text: {
      primary: "rgba(0, 0, 0, 0.95)",
      secondary: "rgba(0, 0, 0, 0.5)",
    },
    background: {
      default: "#F7F9FC",
      paper: "#ffffff",
    },
  },
  sidebar: {
    color: "#FFF",
    background: customBlue[700],
    header: {
      color: "#FFF",
      background: customBlue[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: customBlue[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background:
      "linear-gradient(180deg, rgba(216, 223, 247, 1) 0%, rgba(255, 255, 255, 1) 60%)",
  },
});

const greenVariant = merge(defaultVariant, {
  name: THEMES.GREEN,
  palette: {
    primary: {
      main: green[800],
      contrastText: "#FFF",
    },
    secondary: {
      main: green[500],
      contrastText: "#FFF",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
  },
  header: {
    indicator: {
      background: green[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: green[700],
    header: {
      color: "#FFF",
      background: green[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: green[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background: "linear-gradient(180deg,#C3E9C5 0%,#FFFFFF 60%)",
  },
});

const indigoVariant = merge(defaultVariant, {
  name: THEMES.INDIGO,
  palette: {
    primary: {
      main: indigo[600],
      contrastText: "#FFF",
    },
    secondary: {
      main: indigo[400],
      contrastText: "#FFF",
    },
  },
  header: {
    indicator: {
      background: indigo[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: indigo[700],
    header: {
      color: "#FFF",
      background: indigo[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: indigo[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background:
      "linear-gradient(180deg,#C1C7EB 0%, rgba(255, 255, 255, 1) 60%)",
  },
});

const redVariant = merge(defaultVariant, {
  name: THEMES.RED,
  palette: {
    primary: {
      main: red[600],
      contrastText: "#FFF",
    },
    secondary: {
      main: red[400],
      contrastText: "#FFF",
    },
  },
  header: {
    indicator: {
      background: red[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: red[700],
    header: {
      color: "#FFF",
      background: red[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: red[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background: "linear-gradient(180deg, #F8CAC9 0%, #FFFFFF 60%)",
  },
});

const deepPurpleVariant = merge(defaultVariant, {
  name: THEMES.DEEP_PURPLE,
  palette: {
    primary: {
      main: deepPurple[600],
      contrastText: "#FFF",
    },
    secondary: {
      main: deepPurple[400],
      contrastText: "#FFF",
    },
  },
  header: {
    indicator: {
      background: deepPurple[600],
    },
  },
  sidebar: {
    color: "#FFF",
    background: deepPurple[700],
    header: {
      color: "#FFF",
      background: deepPurple[800],
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: deepPurple[800],
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background:
      "linear-gradient(180deg, #CFC0EC 0%, rgba(255, 255, 255, 1) 60%)",
  },
});

const yellowVariant = merge(defaultVariant, {
  name: THEMES.YELLOW,
  palette: {
    primary: {
      main: "#154273",
      contrastText: "#fff",
    },
    secondary: {
      main: customBlue[400],
      contrastText: "#000000",
    },
  },
  header: {
    indicator: {
      background: "#FFEB3B",
    },
  },
  sidebar: {
    color: "#000",
    background: "#FFEB3B",
    header: {
      color: "#000",
      background: "#f9e11e",
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: "#FFEB3B",
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background:
      "linear-gradient(180deg,#FFEB3B 0%, rgba(255, 255, 255, 1) 60%)",
  },
});

const blackVariant = merge(defaultVariant, {
  name: THEMES.BLACK,
  palette: {
    primary: {
      main: "#000000",
      contrastText: "#FFF",
    },
    secondary: {
      main: "#424242",
      contrastText: "#FFF",
    },
    background: {
      default: "#ffffff",
      paper: "#ffffff",
    },
  },
  header: {
    indicator: {
      background: "#000000",
    },
  },
  sidebar: {
    color: "#FFF",
    background: "#000000",
    header: {
      color: "#FFF",
      background: "#000000",
      brand: {
        color: "#FFFFFF",
      },
    },
    footer: {
      color: "#FFF",
      background: "#000000",
      online: {
        background: "#FFF",
      },
    },
    badge: {
      color: "#000",
      background: "#FFF",
    },
  },
  chatbot: {
    color: "rgb(216, 223, 247)",
    background: "linear-gradient(180deg, #cccccc 0%, #f2f2f2 60%);",
  },
});

const variants: Array<VariantType> = [
  defaultVariant,
  darkVariant,
  lightVariant,
  blueVariant,
  greenVariant,
  indigoVariant,
  redVariant,
  deepPurpleVariant,
  yellowVariant,
  blackVariant,
];

export default variants;

export type VariantType = {
  name: string;
  palette: {
    primary: MainContrastTextType;
    secondary: MainContrastTextType;
  };
  header: ColorBgType & {
    search: {
      color: string;
    };
    indicator: {
      background: string;
    };
  };
  footer: ColorBgType;
  sidebar: ColorBgType & {
    header: ColorBgType & {
      brand: {
        color: string;
      };
    };
    footer: ColorBgType & {
      online: {
        background: string;
      };
    };
    badge: ColorBgType;
  };
  chatbot: ColorBgType;
};

type MainContrastTextType = {
  main: string;
  contrastText: string;
};
type ColorBgType = {
  color: string;
  background: string;
};
